import * as THREE from 'three';
import { PLANET_RADIUS } from './Planet';

const Y = new THREE.Vector3(0, 1, 0);
const X = new THREE.Vector3(1, 0, 0);

/**
 * Polar coordinates around a pole on the planet: theta = angle from the pole,
 * phi = azimuth. Using the same tangent basis for a point and its antipode
 * means a meridian keeps its phi on both sides of the world.
 */
export class PolarFrame {
  readonly pole: THREE.Vector3;
  readonly t1: THREE.Vector3;
  readonly t2: THREE.Vector3;

  constructor(pole: THREE.Vector3, basisFrom?: PolarFrame) {
    this.pole = pole.clone().normalize();
    if (basisFrom) {
      this.t1 = basisFrom.t1.clone();
      this.t2 = basisFrom.t2.clone();
    } else {
      const ref = Math.abs(this.pole.y) < 0.9 ? Y : X;
      this.t1 = new THREE.Vector3().crossVectors(ref, this.pole).normalize();
      this.t2 = new THREE.Vector3().crossVectors(this.pole, this.t1);
    }
  }

  dir(theta: number, phi: number): THREE.Vector3 {
    return this.pole.clone().multiplyScalar(Math.cos(theta))
      .addScaledVector(this.t1, Math.cos(phi) * Math.sin(theta))
      .addScaledVector(this.t2, Math.sin(phi) * Math.sin(theta))
      .normalize();
  }

  /** Arc length from the pole in world units. */
  arc(d: THREE.Vector3): number {
    return d.clone().normalize().angleTo(this.pole) * PLANET_RADIUS;
  }

  theta(d: THREE.Vector3): number {
    return d.clone().normalize().angleTo(this.pole);
  }

  phi(d: THREE.Vector3): number {
    const n = d.clone().normalize();
    return Math.atan2(n.dot(this.t2), n.dot(this.t1));
  }
}

/** Great-circle surface distance between two directions, in world units. */
export function arcDist(a: THREE.Vector3, b: THREE.Vector3): number {
  return a.angleTo(b) * PLANET_RADIUS;
}

/** A circular keep-out area on the surface (buildings, stalls). */
export type SphereObstacle = { dir: THREE.Vector3; r: number };

const tq = new THREE.Quaternion();
const ta = new THREE.Vector3();

/**
 * Something that walks or hovers over the surface: a unit direction plus a
 * tangent heading. Moves along great circles and slides off obstacles.
 */
export class SurfaceMover {
  readonly dir: THREE.Vector3;
  readonly heading: THREE.Vector3;

  constructor(dir: THREE.Vector3, heading?: THREE.Vector3) {
    this.dir = dir.clone().normalize();
    this.heading = (heading ?? new THREE.Vector3(1, 0, 0)).clone().projectOnPlane(this.dir);
    if (this.heading.lengthSq() < 1e-6) this.heading.copy(X).projectOnPlane(this.dir);
    this.heading.normalize();
  }

  /** Unit tangent pointing from here toward `target`. */
  toward(target: THREE.Vector3, out = new THREE.Vector3()): THREE.Vector3 {
    out.copy(target).normalize().projectOnPlane(this.dir);
    return out.lengthSq() < 1e-9 ? out.copy(this.heading) : out.normalize();
  }

  /** Turn the heading toward a tangent direction by at most `maxRad`. */
  turn(want: THREE.Vector3, maxRad: number): void {
    const a = Math.acos(THREE.MathUtils.clamp(this.heading.dot(want), -1, 1));
    if (a < 1e-4) return;
    const s = Math.sign(ta.crossVectors(this.heading, want).dot(this.dir)) || 1;
    this.heading.applyAxisAngle(this.dir, s * Math.min(a, maxRad)).projectOnPlane(this.dir).normalize();
  }

  /** Move `dist` world units along the heading, then push out of obstacles. */
  advance(dist: number, obstacles: SphereObstacle[] = [], selfR = 0.4): void {
    if (Math.abs(dist) > 1e-6) {
      tq.setFromAxisAngle(ta.crossVectors(this.dir, this.heading).normalize(), dist / PLANET_RADIUS);
      this.dir.applyQuaternion(tq).normalize();
      this.heading.applyQuaternion(tq);
    }
    for (const o of obstacles) {
      const min = (o.r + selfR) / PLANET_RADIUS;
      const a = this.dir.angleTo(o.dir);
      if (a < min && a > 1e-6) {
        const axis = ta.crossVectors(o.dir, this.dir).normalize();
        this.dir.copy(o.dir).applyAxisAngle(axis, min);
      }
    }
    this.heading.projectOnPlane(this.dir).normalize();
  }

  /** Orient an object upright at this spot, facing the heading. */
  orient(obj: THREE.Object3D, worldPos: THREE.Vector3): void {
    obj.position.copy(worldPos);
    const x = new THREE.Vector3().crossVectors(this.dir, this.heading).normalize();
    obj.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, this.dir, this.heading));
  }
}
